import React, { useState } from "react";
import Input from "form/Inputs/Input";
import SelectInput from "form/Inputs/SelectInput";
import FileInput from "form/Inputs/FileInput";
import BASE_URL from "Hooks/BASE_URL";

const JobApplicationForm = ({ jobsData }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    location: "",
    contract: "",
    jobFamily: "",
    cv: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: string }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear notification when user starts interacting with form
    if (notification) {
      setNotification(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({
      ...prev,
      cv: file,
    }));

    // Clear error when file is selected
    if (errors.cv) {
      setErrors((prev) => ({
        ...prev,
        cv: "",
      }));
    }

    // Clear notification when user starts interacting with form
    if (notification) {
      setNotification(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.location) {
      newErrors.location = "Please select a location";
    }

    if (!formData.contract) {
      newErrors.contract = "Please select a contract type";
    }

    if (!formData.jobFamily) {
      newErrors.jobFamily = "Please select a job family";
    }

    if (!formData.cv) {
      newErrors.cv = "Please upload your CV";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Create FormData object for file upload
        const formDataToSend = new FormData();
        formDataToSend.append("first_name", formData.firstName);
        formDataToSend.append("last_name", formData.lastName);
        formDataToSend.append("location_id", formData.location);
        formDataToSend.append("contract_id", formData.contract);
        formDataToSend.append("job_family_id", formData.jobFamily);
        formDataToSend.append("cv_file", formData.cv);

        // Log FormData contents
        for (let [key, value] of formDataToSend.entries()) {
          console.log(key, value);
        }

        // Send POST request as a public form submission
        const response = await fetch(`${BASE_URL}job-enquiry`, {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formDataToSend,
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Server response:", response.status, errorText);
          throw new Error(
            `Failed to submit application: ${response.status} - ${errorText}`
          );
        }

        const result = await response.json();
        console.log("Application submitted successfully:", result);

        // Show success notification
        setNotification({
          type: "success",
          message:
            "Your application has been submitted successfully! We will review it and get back to you soon.",
        });

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          location: "",
          contract: "",
          jobFamily: "",
          cv: null,
        });

        // Reset file input
        const fileInput = document.getElementById("cv");
        if (fileInput) {
          fileInput.value = "";
        }

        // Clear errors
        setErrors({});

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      } catch (error) {
        console.error("Error submitting application:", error);

        // Show error notification
        setNotification({
          type: "error",
          message:
            "Failed to submit your application. Please check your information and try again.",
        });

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setNotification(null);
        }, 5000);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Prepare options for dropdowns
  const locationOptions =
    jobsData?.locations?.map((location) => ({
      value: location.id,
      label: location.name,
    })) || [];

  const contractOptions =
    jobsData?.contracts?.map((contract) => ({
      value: contract.id,
      label: contract.name,
    })) || [];

  const jobFamilyOptions =
    jobsData?.job_families?.map((family) => ({
      value: family.id,
      label: family.name,
    })) || [];

  return (
    <section className="py-24 scroll-section-special">
      <h6 className="bebas text-5xl lg:text-6xl lg:text-center text-white mb-10">
        Apply for a Job
      </h6>

      {/* Notification Component */}
      {notification && (
        <div
          className={`max-w-2xl mx-auto mb-6 p-4 rounded-sm border-l-4 ${
            notification.type === "success"
              ? "bg-green-900/50 border-green-500 text-green-100"
              : "bg-red-900/50 border-red-500 text-red-100"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div
                className={`w-5 h-5 mr-3 ${
                  notification.type === "success"
                    ? "text-green-400"
                    : "text-red-400"
                }`}
              >
                {notification.type === "success" ? (
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <p className="font-medium">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification(null)}
              className={`ml-4 text-xl font-bold hover:opacity-70 ${
                notification.type === "success"
                  ? "text-green-400"
                  : "text-red-400"
              }`}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className=" mx-auto">
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="text-white bg-[#212121] p-6 lg:py-8 lg:px-16">
            <h3 className="text-2xl mb-6 text-font">Personal Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Input
                label="First Name"
                name="firstName"
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                hasError={!!errors.firstName}
                errorMessage={errors.firstName}
                placeholder="Enter your first name"
              />

              <Input
                label="Last Name"
                name="lastName"
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                hasError={!!errors.lastName}
                errorMessage={errors.lastName}
                placeholder="Enter your last name"
              />
            </div>

            <h3 className="text-2xl mb-6 text-font">Job Preferences</h3>

            <div className="flex flex-col lg:flex-row gap-4 items-center">
              <SelectInput
                label="Location"
                name="location"
                id="location"
                value={formData.location}
                onChange={handleInputChange}
                options={[
                  { value: "", label: "Select a location" },
                  ...locationOptions,
                ]}
                hasError={!!errors.location}
                errorMessage={errors.location}
              />

              <SelectInput
                label="Contract Type"
                name="contract"
                id="contract"
                value={formData.contract}
                onChange={handleInputChange}
                options={[
                  { value: "", label: "Select contract type" },
                  ...contractOptions,
                ]}
                hasError={!!errors.contract}
                errorMessage={errors.contract}
              />

              <SelectInput
                label="Job Family"
                name="jobFamily"
                id="jobFamily"
                value={formData.jobFamily}
                onChange={handleInputChange}
                options={[
                  { value: "", label: "Select job family" },
                  ...jobFamilyOptions,
                ]}
                hasError={!!errors.jobFamily}
                errorMessage={errors.jobFamily}
              />
            </div>
          </div>
          <div className="text-white bg-[#212121] p-6 lg:py-8 lg:px-16">
            <h3 className="text-2xl mb-6 text-font">Document Upload</h3>

            <FileInput
              label="Upload CV"
              name="cv"
              id="cv"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              hasError={!!errors.cv}
              errorMessage={errors.cv}
            />

            <p className="text-sm text-gray-400 mt-2">
              Accepted formats: PDF, DOC, DOCX (Max 5MB)
            </p>
          </div>

          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-font text-white px-8 py-3 rounded-sm hover:bg-opacity-90 transition-colors duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default JobApplicationForm;
